// @flow

// This captures all parameters (or flags) that are passed by the
// user. It'll capture and group various styles of user-provided
// flags, such as -a, -b foo, --c=foo, --d bar
//
// todo: this regex is a bit of a hack to fix a bug that came up
// between single and double quotes. The two value_quote groups
// exist solely to capture both permutations of users passing in
// either single or double quotes. Ideally there's one regex that
// gracefully can handle both.
export const flagsRegex = /(?<flag>-+[a-zA-Z0-9-]+)(?:\s|=)(?:(?<value_data>"[^"]+"=(?:"[^"]+"|[^"\s]+))|(?:")(?<value_dQuote>[^"]+?)(?:")|(?:')(?<value_sQuote>[^']+?)(?:')|(?<value_noQuote>[^-"][^"\s]*))?/g;

// This captures all "enviornment" variables, which is anything that
// is an all-caps assignment, such as FOO=bar, FOO="bar", etc
export const envRegex = /(?<env>[A-Z][A-Z0-9_]+)(?:\s|=)(?:(?<value_data>"[^"]+"=(?:"[^"]+"|[^"\s]+))|(?:")(?<value_quote>[^"]+)(?:")|(?<value_noQuote>[^-"][^"\s]*))?/g;

export class InputCommand {
  env: {[string]: string};

  executable: string;

  arguments: Array<string>;

  flags: Array<string>;

  isHelp: boolean;

  constructor(input: string) {
    this.env = {};
    // todo(tomer) - shoudl this be null? empty string makes the
    // type checking easier
    this.executable = '';
    this.arguments = [];
    this.flags = [];
    this.isHelp = false;

    this.parseInput(input);
  }

  parseInput(input: string) {
    // Pull out all the parts that look like environment variables
    // and then the rest of the input _minus_ all of the env vars
    const {
      inputMinusEnv,
      envVars,
    }: {
      inputMinusEnv: string,
      envVars: {[string]: string},
    } = this.extractEnvVars(input);
    this.env = envVars;

    // Pull out everything that looks like a flag and the rest of the
    // input minus the flags for next steps
    const {inputMinusFlags, flags} = this.extractFlags(inputMinusEnv);
    this.flags = flags;

    // Work through each flag to clean it up and see if the user
    // passed in the help flags
    this.flags.forEach((flag) => {
      if (flag.trim() === '--help' || flag.trim() === '-h') {
        this.isHelp = true;
      }
    });

    const parts = inputMinusFlags.split(/\s+/g);
    parts.forEach((part, i) => {
      let cleanPart = part.trim();

      if (cleanPart === '') {
        return;
      }

      // If we do not yet have the executable, that should be the first thing
      if (this.executable === '') {
        this.executable = cleanPart;

        // This can't be anything else so on to the next part
        return;
      }

      // Special case for help commands, we want to capture a "help"
      // argument or flag
      if (
        cleanPart === 'help' ||
        cleanPart === '--help' ||
        cleanPart === '-h'
      ) {
        this.isHelp = true;

        // todo - the flags regex above does not correctly capture
        // --help or -h so as a hack check for it here
        if (cleanPart.startsWith('-')) {
          this.flags.push(cleanPart);
          return;
        }
      }

      // If the arg given is wrapped in quotes, remove it. Note: this
      // doesn't really handle the situation where the entire quoted
      // arg needs to be passed in so things with a space will break
      // todo -- fix ^, make this a regex (or something better)
      if (
        (cleanPart.startsWith(`"`) && cleanPart.endsWith(`"`)) ||
        (cleanPart.startsWith(`'`) && cleanPart.endsWith(`'`))
      ) {
        cleanPart = cleanPart.substring(1, cleanPart.length - 1);
      }

      // Anything remaining should be an argument for the executable
      this.arguments.push(cleanPart);
    });
  }

  isEnvVariable(variable: string): boolean {
    const parts = variable.split('=');

    // Make sure that this is a variable assignment and that there's
    // a value on the right side. Also, check that the left side is
    // an "environment variable" by it being all caps
    if (parts.length !== 2 && parts[0] !== parts[0].toUpperCase()) {
      return false;
    }

    return true;
  }

  // Render the executable + "sub-commands" (which may just be arguments)
  renderInvocation(): string {
    return `${this.executable} ${this.arguments.join(' ')}`;
  }

  // Search for environment variables and build a name of the name
  // and value
  extractEnvVars(
    command: string,
  ): {inputMinusEnv: string, envVars: {[string]: string}} {
    const envMatches = command.match(envRegex);
    const envVars = {};

    if (envMatches) {
      envMatches.forEach((envVar) => {
        const envVarSplit = envVar.split('=');
        const name = envVarSplit[0];

        // Remove starting end ending double/single quotes
        const value = envVarSplit[1].replace(/^("|')|("|')$/g, '');
        envVars[name] = value;
      });
    }

    return {
      inputMinusEnv: command.replace(envRegex, ''),
      envVars,
    };
  }

  // Pull out all user-provided flags from the command. Return the
  // command without the flags and then the array of flags extracted
  extractFlags(
    command: string,
  ): {inputMinusFlags: string, flags: Array<string>} {
    return {
      inputMinusFlags: command.replace(flagsRegex, ''),
      flags: command.match(flagsRegex) || [],
    };
  }
}
